import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Edit2, Trash2, Plus } from 'lucide-react';
import { Status, DEFAULT_STATUSES } from './types';
import { StatusCleanupButton } from './StatusCleanupButton';

interface StatusManagerProps {
  open: boolean;
  onClose: () => void;
  statuses: Status[];
  onAdd: (status: Omit<Status, 'id'>) => Promise<void>;
  onUpdate: (id: string, status: Partial<Status>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export const StatusManager: React.FC<StatusManagerProps> = ({
  open,
  onClose,
  statuses,
  onAdd,
  onUpdate,
  onDelete
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    label: string;
    color: string;
    variant: 'outlined' | 'default' | 'filled';
  }>({
    name: '',
    label: '',
    color: '#2196F3',
    variant: 'default'
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleOpenDialog = (status?: Status): void => {
    if (status) {
      setEditingStatus(status);
      setFormData({
        name: status.name,
        label: status.label,
        color: status.color,
        variant: status.variant
      });
    } else {
      setEditingStatus(null);
      setFormData({
        name: '',
        label: '',
        color: '#2196F3',
        variant: 'default'
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!formData.name.trim() || !formData.label.trim()) return;

    setIsSubmitting(true);
    try {
      if (editingStatus) {
        await onUpdate(editingStatus.id, formData);
      } else {
        await onAdd(formData);
      }
      setIsDialogOpen(false);
      setFormData({
        name: '',
        label: '',
        color: '#2196F3',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error saving status:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDefaultStatus = (statusName: string): boolean => {
    return DEFAULT_STATUSES.some(s => s.name === statusName);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Statuses</DialogTitle>
      <DialogContent>
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 2
        }}>
          <Button
            startIcon={<Plus size={16} />}
            onClick={() => handleOpenDialog()}
            variant="outlined"
            size="small"
          >
            Add Status
          </Button>
          <StatusCleanupButton />
        </Box>

        <List>
          {statuses.map((status) => (
            <ListItem key={status.id}>
              <ListItemText
                primary={status.label}
                secondary={`Type: ${status.name}`}
              />
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: status.color,
                  mr: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleOpenDialog(status)}
                  sx={{ mr: 1 }}
                >
                  <Edit2 size={16} />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => onDelete(status.id)}
                  disabled={isDefaultStatus(status.name)}
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.3
                    }
                  }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingStatus ? 'Edit Status' : 'Add Status'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Status Name"
                value={formData.name}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  name: e.target.value.toUpperCase().replace(/\s+/g, '_')
                })}
                helperText="This will be used as the internal identifier"
                disabled={!!(editingStatus && isDefaultStatus(editingStatus.name))}
              />
              <TextField
                fullWidth
                label="Display Label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                helperText="This is what users will see"
              />
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Variant</InputLabel>
                <Select
                  value={formData.variant}
                  label="Variant"
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variant: e.target.value as 'outlined' | 'default' | 'filled' 
                  })}
                >
                  <MenuItem value="outlined">Outlined</MenuItem>
                  <MenuItem value="default">Default</MenuItem>
                  <MenuItem value="filled">Filled</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting || !formData.name.trim() || !formData.label.trim()}
            >
              {isSubmitting
                ? 'Saving...'
                : editingStatus
                ? 'Save Changes'
                : 'Add Status'}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};